/**
 * QUIPWA — Drive errors
 *
 * Every failure that leaves the Drive layer is a `DriveError` with a `kind`.
 * Callers branch on the kind, never on raw status codes, and — the reason this
 * module exists — **only `not_found` may ever lead to creating something**.
 *
 * Before, `findFile` returned `null` for any non-2xx response, so a 429 or a
 * 503 looked exactly like "the file doesn't exist" and the app created a second
 * `QUIPWA` folder or a second `_state.db`. See docs/modulos/drive.md.
 */

export type DriveErrorKind =
  /** 404 — the file/folder does not exist (or is not visible to this app). */
  | 'not_found'
  /** 401 — the access token is missing, expired or revoked. Needs re-auth. */
  | 'unauthorized'
  /** 403 — the token is valid but lacks a scope (e.g. `drive.appdata`). Needs consent. */
  | 'insufficient_scope'
  /** 403 — the user's Drive is full. Not retryable; the user must free space. */
  | 'quota_exceeded'
  /** 403/429 — rate limited. Retryable with backoff. */
  | 'rate_limited'
  /** 403 — any other permission problem. */
  | 'forbidden'
  /** 409/412 — a precondition failed (concurrent modification). */
  | 'conflict'
  /** 400 — the request itself is wrong. A bug, not a condition to retry. */
  | 'bad_request'
  /** No network at all (`navigator.onLine === false` and fetch failed). */
  | 'offline'
  /** 5xx or a dropped connection while online. Retryable. */
  | 'transient'
  /** Anything else. */
  | 'unknown'

const RETRYABLE: ReadonlySet<DriveErrorKind> = new Set(['rate_limited', 'transient'])

export class DriveError extends Error {
  readonly kind: DriveErrorKind
  readonly status: number | null
  /** Google's machine-readable reason, e.g. `userRateLimitExceeded`. */
  readonly reason: string | null
  /** Seconds the server asked us to wait (from `Retry-After`), if any. */
  readonly retryAfterSeconds: number | null

  constructor(
    kind: DriveErrorKind,
    message: string,
    options: {
      status?: number | null
      reason?: string | null
      retryAfterSeconds?: number | null
      cause?: unknown
    } = {}
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause })
    this.name = 'DriveError'
    this.kind = kind
    this.status = options.status ?? null
    this.reason = options.reason ?? null
    this.retryAfterSeconds = options.retryAfterSeconds ?? null
  }

  /** Whether repeating the same request later can succeed. */
  get retryable(): boolean {
    return RETRYABLE.has(this.kind)
  }
}

/** Type guard, optionally narrowed to one or more kinds. */
export function isDriveError(err: unknown, ...kinds: DriveErrorKind[]): err is DriveError {
  return err instanceof DriveError && (kinds.length === 0 || kinds.includes(err.kind))
}

// ─── CLASSIFICATION ──────────────────────────────────────────────────────────

/** Shape of Google's JSON error body (only the parts we read). */
interface GoogleErrorBody {
  error?: {
    message?: string
    status?: string
    errors?: Array<{ reason?: string; message?: string }>
  }
}

function parseGoogleError(bodyText: string): { message: string | null; reason: string | null; status: string | null } {
  try {
    const body = JSON.parse(bodyText) as GoogleErrorBody
    return {
      message: body.error?.message ?? null,
      reason: body.error?.errors?.[0]?.reason ?? null,
      status: body.error?.status ?? null,
    }
  } catch {
    return { message: null, reason: null, status: null }
  }
}

const RATE_LIMIT_REASONS = new Set(['rateLimitExceeded', 'userRateLimitExceeded', 'sharingRateLimitExceeded'])
const QUOTA_REASONS = new Set(['storageQuotaExceeded', 'quotaExceeded', 'teamDriveFileLimitExceeded'])

function parseRetryAfter(value: string | null): number | null {
  if (!value) return null
  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) return seconds
  const date = Date.parse(value)
  if (Number.isNaN(date)) return null
  return Math.max(0, Math.round((date - Date.now()) / 1000))
}

/**
 * Turn a non-2xx HTTP response into a `DriveError`.
 *
 * @param status      HTTP status code
 * @param bodyText    raw response body (may be empty or non-JSON)
 * @param retryAfter  value of the `Retry-After` header, if present
 * @param context     short description for the message, e.g. `files.get abc123`
 */
export function classifyHttpError(
  status: number,
  bodyText: string,
  retryAfter: string | null,
  context: string
): DriveError {
  const { message, reason, status: googleStatus } = parseGoogleError(bodyText)
  const detail = message ? `: ${message}` : ''
  const msg = `Drive ${context} → HTTP ${status}${detail}`
  const opts = { status, reason, retryAfterSeconds: parseRetryAfter(retryAfter) }

  if (status === 404) return new DriveError('not_found', msg, opts)
  if (status === 401) return new DriveError('unauthorized', msg, opts)
  if (status === 429) return new DriveError('rate_limited', msg, opts)
  if (status === 409 || status === 412) return new DriveError('conflict', msg, opts)
  if (status === 400) return new DriveError('bad_request', msg, opts)
  if (status === 403) {
    if (reason && RATE_LIMIT_REASONS.has(reason)) return new DriveError('rate_limited', msg, opts)
    if (reason && QUOTA_REASONS.has(reason)) return new DriveError('quota_exceeded', msg, opts)
    const scopeProblem =
      reason === 'insufficientPermissions' ||
      reason === 'ACCESS_TOKEN_SCOPE_INSUFFICIENT' ||
      (googleStatus === 'PERMISSION_DENIED' && /scope/i.test(message ?? ''))
    if (scopeProblem) return new DriveError('insufficient_scope', msg, opts)
    return new DriveError('forbidden', msg, opts)
  }
  if (status >= 500) return new DriveError('transient', msg, opts)
  return new DriveError('unknown', msg, opts)
}

/**
 * Classify an exception thrown by `fetch` itself (no HTTP response at all).
 * `fetch` rejects with a `TypeError` on DNS failure, dropped connection, CORS…
 */
export function classifyNetworkError(err: unknown, context: string, online: boolean): DriveError {
  if (err instanceof DriveError) return err
  const kind: DriveErrorKind = online ? 'transient' : 'offline'
  const reason = err instanceof Error ? err.message : String(err)
  return new DriveError(kind, `Drive ${context} → ${online ? 'error de red' : 'sin conexión'} (${reason})`, {
    cause: err,
  })
}
